import { useCallback, useRef } from "react";
import { Button, Icon, Text, useMediaQuery } from "@salestrak/ui";
import {
  DetailViewCloseButton,
  DetailViewFavoriteButton,
  DetailViewHeaderRow,
  DetailViewPopup,
  DetailViewSizeToggle,
  detailViewParts,
  type DetailViewParts,
  type DetailViewSize,
} from "#app/components/detail-view/detail-view-popup";
import { ContainEntityDetail, useYieldToEntityDetail } from "#app/components/entity";
import { IssueMenu } from "#app/components/issue-actions/issue-menu";
import type { IssueRow } from "#app/lib/data";
import { useIssueCardActions } from "#app/hooks/kanban/use-issue-card-actions";
import { useIssueViewData } from "#app/hooks/issue-view/use-issue-view-data";
import {
  CreateIssueProvider,
  useCreateIssueMode,
} from "#app/lib/create-issue-context";
import { useItemShareDialogActions } from "#app/lib/item-share-dialog-context";
import { canManageSharing } from "#app/lib/utils/access-level";
import { IssueBody } from "./issue-body";
import { useIssueViewDialogState } from "./issue-view-dialog-state";
import { IssueBreadcrumb } from "./issue-view-header";

const FULLSCREEN_MAX_WIDTH = 960;
// Below this window width the header goes tight: the standalone Favorite
// star drops out of the corner cluster (it still lives in the "…" menu).
const TIGHT_HEADER_WINDOW = 900;

function IssueViewCornerActions({
  issue,
  desktopSize,
  setDesktopSize,
  onClose,
  compact,
  allowFullscreen,
  closeAsPanel,
}: {
  issue: IssueRow;
  desktopSize: DetailViewSize;
  setDesktopSize: (size: DetailViewSize) => void;
  onClose: () => void;
  /** Tight quarters (drawer, narrow window): drop the standalone Favorite
   *  star — it already rides the "…" menu — so the breadcrumb keeps the row. */
  compact?: boolean;
  allowFullscreen: boolean;
  closeAsPanel: boolean;
}) {
  const { toggleFavorite, isIssueFavorited } = useIssueCardActions();
  const { openDialog: openShareDialog } = useItemShareDialogActions();
  const isFavorited = isIssueFavorited(issue.issueId);
  // The corner menu lives outside the IssueViewCapabilitiesProvider
  // (which is mounted inside IssueBody), so we read the gates directly off
  // the IssueRow: `viewerCanEdit` is the server's own edit decision (role
  // floor + access level), `viewerAccessLevel` the displayed sharing tier.
  const readOnly = !issue.viewerCanEdit;
  // Only `full_edit` can manage the share list. Mirrors the gate on the
  // three-dot menu's "Sharing & Permissions" action.
  const canShare = canManageSharing(issue.viewerAccessLevel);
  return (
    <>
      {canShare && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => openShareDialog(issue.issueId)}
        >
          <Icon name="users" size="sm" color="muted" />
          <Text variant="muted">Share</Text>
        </Button>
      )}
      <IssueMenu
        issue={issue}
        readOnly={readOnly}
        triggerTooltip="More actions"
      />
      {!compact && (
        <DetailViewFavoriteButton
          favorited={isFavorited}
          onToggle={() => toggleFavorite(issue.issueId)}
          subject="project"
        />
      )}
      {allowFullscreen ? (
        <DetailViewSizeToggle
          desktopSize={desktopSize}
          setDesktopSize={setDesktopSize}
        />
      ) : null}
      <DetailViewCloseButton
        asPanel={closeAsPanel}
        panelLabel="Close Project panel"
        onClose={onClose}
      />
    </>
  );
}

export function IssueViewDialog({ nested = false }: { nested?: boolean }) {
  const dialog = useIssueViewDialogState();
  const nestedState =
    dialog.state?.mode === "view" &&
    dialog.state.presentation === "fullscreen-dialog";
  // Each host keeps ownership through its close transition. The global host
  // owns ordinary project views; a dialog boundary owns contextual views.
  const ownsPresentation = useRef(!nested);
  if (dialog.state) ownsPresentation.current = nested === nestedState;
  if (!ownsPresentation.current) return null;

  return <IssueViewDialogSurface nested={nested} />;
}

function IssueViewDialogSurface({ nested }: { nested: boolean }) {
  const dialog = useIssueViewDialogState();

  const viewState = dialog.state?.mode === "view" ? dialog.state : null;
  const createState = dialog.state?.mode === "create" ? dialog.state : null;
  const isOpen = !!dialog.state;

  const viewIssueId = viewState?.issue.issueId;
  // The frame (title, breadcrumb, actions) reads the live project, as the
  // body does. The copy taken at open goes stale after a rename or a move
  // to another workflow. The body runs the same query, so this adds no
  // request.
  const { issue: liveIssue } = useIssueViewData(viewIssueId);
  const headerIssue = viewState
    ? ((liveIssue as IssueRow | null) ?? (viewState.issue as IssueRow))
    : null;

  // A contextual project is a nested fullscreen dialog. Ordinary project
  // opens continue to honor the viewer's stored sidebar/fullscreen preference.
  const isDrawerMode = !nested && dialog.desktopSize === "sidebar";
  // Tight header: the drawer always (it shares the row with the page beside
  // it), or a window narrow enough that the labeled corner cluster and the
  // breadcrumb fight over the row.
  const isTightWindow = useMediaQuery({ max: TIGHT_HEADER_WINDOW });
  const tightHeader = isDrawerMode || isTightWindow;

  // The drawer's content fills the drawer (inside its own padding), so it
  // tracks every width the drag reaches. Fullscreen keeps a reading measure.
  const contentMaxWidth = isDrawerMode ? undefined : FULLSCREEN_MAX_WIDTH;

  const close = useCallback(() => dialog.setState(null), [dialog]);

  // A chip press that opens the profile island (or a team page) closes this
  // view when it presents as a MODAL — the island renders in the shell,
  // beneath any modal backdrop. The drawer coexists with the island, and a
  // create draft never yields (its people controls are pickers, and a chip
  // press must not discard typed input).
  useYieldToEntityDetail(!!viewState && !isDrawerMode, close);

  const bodyProps = {
    activeTab: dialog.activeTab,
    setActiveTab: dialog.setActiveTab,
    contentMaxWidth,
    isMobile: false,
  };

  const parts = detailViewParts(isDrawerMode);

  const content = (
    <>
      {viewState && (
        <>
          <DetailViewHeaderRow
            parts={parts}
            title={headerIssue?.name ?? "Issue"}
            breadcrumb={
              <IssueBreadcrumb
                issue={headerIssue ?? undefined}
              />
            }
            actions={
              <IssueViewCornerActions
                issue={headerIssue ?? (viewState.issue as IssueRow)}
                desktopSize={dialog.desktopSize}
                setDesktopSize={dialog.setDesktopSize}
                onClose={close}
                compact={tightHeader}
                allowFullscreen={!nested}
                closeAsPanel={isDrawerMode}
              />
            }
          />
          <IssueBody
            {...bodyProps}
            issue={viewState.issue}
            focusNoteId={viewState.focusNoteId}
            focusUnread={viewState.focusUnread}
            focusCustomFieldId={viewState.focusCustomFieldId}
            focusTitleNonce={viewState.focusTitleNonce}
            inlineComposer
          />
        </>
      )}

      {createState && (
        <CreateIssueProvider
          key={
            createState.address?.wholeAddress ??
            (createState.address?.latLng
              ? `${createState.address.latLng.lat},${createState.address.latLng.lng}`
              : "no-address")
          }
          initialWorkflowId={createState.workflowId}
          initialStageId={createState.stageId}
          initialAddress={createState.address}
          initialName={createState.name}
          initialIssueSourceId={createState.issueSourceId}
          initialTeamId={createState.teamId}
          source={createState.source}
          onCreated={dialog.onCreated}
        >
          <ContainEntityDetail>
            <DetailViewHeaderRow
              parts={parts}
              title="Create a new Project"
              breadcrumb={<IssueBreadcrumb />}
              actions={
                <>
                  {!nested ? (
                    <DetailViewSizeToggle
                      desktopSize={dialog.desktopSize}
                      setDesktopSize={dialog.setDesktopSize}
                    />
                  ) : null}
                  <DetailViewCloseButton
                    asPanel={isDrawerMode}
                    panelLabel="Close Project panel"
                    onClose={close}
                  />
                </>
              }
            />
            <IssueBody {...bodyProps} issue={null} />
            <CreateFooter parts={parts} closeIssue={dialog.closeIssue} />
          </ContainEntityDetail>
        </CreateIssueProvider>
      )}
    </>
  );

  return (
    <DetailViewPopup
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) dialog.setState(null);
      }}
      drawer={isDrawerMode}
      resizeLabel="Resize Project panel"
      bottomStickOnMobile={!nested}
    >
      {content}
    </DetailViewPopup>
  );
}

function CreateFooter({
  parts,
  closeIssue,
}: {
  parts: DetailViewParts;
  closeIssue: () => void;
}) {
  const { Footer } = parts;
  const { submit, isSubmitting } = useCreateIssueMode()!;
  return (
    <Footer>
      <Button variant="outline" onClick={closeIssue} disabled={isSubmitting}>
        Cancel
      </Button>
      <Button onClick={submit} disabled={isSubmitting} loading={isSubmitting}>
        Create
      </Button>
    </Footer>
  );
}
